import React, { FC, useState } from 'react';

import { Box } from '@mui/material';
import { styled } from '@mui/material';
import { Configure, useClearRefinements, useCurrentRefinements } from 'react-instantsearch';
import { useLocation, useNavigate } from 'react-router-dom';

import TableEmptyState from '~/components/CustomTable/TableEmptyState';
import CustomButton from '~/components/UI/CustomButton/CustomButton';
import { useDiscoverContext } from '~/context/DiscoverContext';
import useMultilingualSearchableAttributes from '~/hooks/useMultilingualSearchableAttributes';
import useTenantSearchFilter from '~/hooks/useTenantSearchFilter';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { InfiniteHits as CustomInfiniteHits } from '~/pages/DiscoverPage/components/InfiniteHits/InfiniteHits';
import { SearchResults } from '~/pages/DiscoverPage/components/SearchResults/SearchResults';
import { LIST_VIEW } from '~/pages/DiscoverPage/const';
import { DISCOVER_SEARCH_ROUTE } from '~/routes';
import { LARGE_MARGIN, LARGE_MARGIN_PX, SUBMIDDLE_MARGIN_PX } from '~/theme';

const searchAttributesConfig = [
  {
    attr: 'title',
    multilingual: true,
  },
  {
    attr: 'user',
    multilingual: false,
  },
  {
    attr: 'categories_array.title',
    multilingual: true,
  },
  {
    attr: 'categories_array.parent.title',
    multilingual: true,
  },
  {
    attr: 'key_learning_objectives',
    multilingual: true,
  },
  {
    attr: 'description',
    multilingual: true,
  },
  {
    attr: 'description_full',
    multilingual: true,
  },
  {
    attr: 'executive_summary',
    multilingual: true,
  },
];

interface ResultsPanelProps {
  linkedInId?: string | null;
  loading: boolean;
}

const ResultsPanel: FC<ResultsPanelProps> = ({ linkedInId, loading }) => {
  const { t } = useTenantTranslation();
  const { discoverView, searchState } = useDiscoverContext();
  const navigate = useNavigate();
  const { refine } = useClearRefinements();
  const { items } = useCurrentRefinements();
  const canRefine = items.length > 0;

  const location = useLocation();
  const search = location?.search;

  const limit = search === '?all' ? 150 : discoverView === LIST_VIEW ? 25 : 24;
  const [itemsCount, setItemsCount] = useState<null | number>(null);
  const [page, setPage] = useState<null | number>(null);

  const searchAttributes = useMultilingualSearchableAttributes(searchAttributesConfig);

  const { tenantFilter } = useTenantSearchFilter();

  const resultsFunc = (count) => {
    return (
      <>
        {t('page.discover.resultFound', { count: count || 0 })}
        {searchState?.query ? ` ${t('page.discover.for')} "${searchState.query}"` : null}
      </>
    );
  };

  const noResultsViewWithRefresh = (
    <>
      <StyledTableEmptyState
        title={t('common.emptyStateTitle')}
        subtitle={
          <>
            {t('error.searchNoResults')}
            <br />
            {t('error.retryWithoutFilters', {
              searchQuery: searchState.query,
            })}
          </>
        }
      />
      {
        <StyledCustomButton
          onClick={() => {
            refine();
            if (searchState.query) {
              navigate(`${DISCOVER_SEARCH_ROUTE}?s=${searchState.query}`);
            }
          }}
        >
          {t('button.retrySearch')}
        </StyledCustomButton>
      }
    </>
  );

  return (
    <Wrapper>
      <Configure
        hitsPerPage={limit}
        enablePersonalization
        clickAnalytics
        restrictSearchableAttributes={searchAttributes}
        filters={tenantFilter}
      />
      <SearchResults
        setItemsCount={setItemsCount}
        setPage={setPage}
        resultsFunc={resultsFunc}
        withToggle
        loading={loading}
      />
      <CustomInfiniteHits
        total={itemsCount}
        page={page}
        limit={limit}
        view={discoverView}
        linkedInId={linkedInId}
        loading={loading}
      />
      {!loading && itemsCount === 0 && (
        <EmptyStateWrapper>
          {canRefine ? (
            noResultsViewWithRefresh
          ) : (
            <StyledTableEmptyState
              title={t('common.emptyStateTitle')}
              subtitle={t('page.discover.emptyStateSubTitle')}
            />
          )}
        </EmptyStateWrapper>
      )}
    </Wrapper>
  );
};

export default ResultsPanel;

const Wrapper = styled(Box)`
  flex: 1;
  margin-bottom: ${LARGE_MARGIN_PX};
`;
const StyledTableEmptyState = styled(TableEmptyState)`
  margin-top: ${LARGE_MARGIN * 2}px;
  height: auto;
`;
const StyledCustomButton = styled(CustomButton)`
  margin: ${SUBMIDDLE_MARGIN_PX} auto 0 auto;
`;
const EmptyStateWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-bottom: ${LARGE_MARGIN * 2}px;
`;
